import { DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { OpenTabSummaryDto, PosTab, PosUserDto } from '@klubovna/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { CheckoutType } from '@posEnums';
import { CheckoutDialogData } from '@posModels';
import { PosOrdersApiService, PosTabsApiService, QrCodeService, RecentOrdersService } from '@posServices';
import { CartItem, CreateOrderDto, PaymentMethod, PaymentType, PosTabType } from '@posTypes';
import { getTabDisplayName } from '@posUtils';
import { NotificationService, PAYMENT_IBAN, SharedModule } from '@shared';
import { finalize, from, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-checkout-dialog',
  standalone: true,
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    TranslateModule,
    SharedModule,
  ],
  templateUrl: './checkout-dialog.component.html',
  styleUrl: './checkout-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<CheckoutDialogComponent>);
  private data = inject<CheckoutDialogData>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private posTabsApi = inject(PosTabsApiService);
  private posOrdersApi = inject(PosOrdersApiService);
  private notification = inject(NotificationService);
  private qrCodeService = inject(QrCodeService);
  private recentOrdersService = inject(RecentOrdersService);

  readonly CheckoutType = CheckoutType;
  readonly PosTabType = PosTabType;
  readonly PaymentMethod = PaymentMethod;
  readonly PaymentType = PaymentType;
  readonly PAYMENT_IBAN = PAYMENT_IBAN;

  items = signal<CartItem[]>([]);
  totalMoneyCents = signal(0);
  totalPoints = signal(0);

  private _checkoutType = signal<CheckoutType>(CheckoutType.WALK_IN);
  checkoutType = computed(() => this._checkoutType());
  paymentMethod = signal<PaymentMethod>(PaymentMethod.Cash);
  newTabType = signal<PosTabType>(PosTabType.User);

  qrCodeDataUrl = signal<string | null>(null);
  currentVariableSymbol = signal<string | null>(null);

  private qrTrigger = computed(() => ({
    isCardPayment: this.paymentMethod() === PaymentMethod.Card,
    isWalkIn: this.checkoutType() === CheckoutType.WALK_IN,
    amount: this.calculatedTotalMoney(),
  }));

  constructor() {
    toObservable(this.qrTrigger).pipe(
      switchMap(({ isCardPayment, isWalkIn, amount }) => {
        if (isCardPayment && isWalkIn && amount > 0) {
          const variableSymbol = this.currentVariableSymbol() || this.qrCodeService.generateVariableSymbol();
          if (!this.currentVariableSymbol()) {
            this.currentVariableSymbol.set(variableSymbol);
          }
          const payBySquareData = this.qrCodeService.generatePayBySquareData(amount, variableSymbol);
          return from(this.qrCodeService.generateQrCodeDataUrl(payBySquareData));
        }
        this.currentVariableSymbol.set(null);
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(dataUrl => {
      this.qrCodeDataUrl.set(dataUrl);
    });
  }

  setCheckoutType(type: CheckoutType): void {
    const previousType = this._checkoutType();
    this._checkoutType.set(type);

    if (previousType === CheckoutType.WALK_IN && type !== CheckoutType.WALK_IN) {
      this.resetItemsToMoney();
    }
  }

  private resetItemsToMoney(): void {
    this.items.update(items =>
      items.map(item => ({
        ...item,
        paymentType: PaymentType.Money,
      })),
    );
    this.selectedWalkInUser.set(null);
    this.walkInUserSearchQuery.set('');
  }

  openTabs = signal<OpenTabSummaryDto[]>([]);
  selectedTab = signal<OpenTabSummaryDto | null>(null);
  tabSearchQuery = signal('');
  tabsLoading = signal(false);

  users = signal<PosUserDto[]>([]);
  selectedUser = signal<PosUserDto | null>(null);
  userSearchQuery = signal('');
  usersLoading = signal(false);

  visitorAlias = signal('');

  submitting = signal(false);

  visitorForm = this.fb.group({
    alias: [ '', Validators.required ],
  });

  moneyItems = computed(() => this.items().filter(i => i.paymentType === PaymentType.Money));
  pointsItems = computed(() => this.items().filter(i => i.paymentType === PaymentType.Points));
  hasPointsItems = computed(() => this.pointsItems().length > 0);
  hasItemsWithPointsOption = computed(() => this.items().some(i => i.canPayWithPoints));

  calculatedTotalMoney = computed(() => {
    return this.moneyItems().reduce((sum, item) => sum + (item.priceCents * item.qty), 0);
  });

  calculatedTotalPoints = computed(() => {
    return this.pointsItems().reduce((sum, item) => sum + (item.pointsCost * item.qty), 0);
  });

  walkInUsers = signal<PosUserDto[]>([]);
  selectedWalkInUser = signal<PosUserDto | null>(null);
  walkInUserSearchQuery = signal('');
  walkInUsersLoading = signal(false);

  walkInUserHasEnoughPoints = computed(() => {
    const user = this.selectedWalkInUser();
    if (!user) return false;
    const balance = user.pointBalance ?? 0;
    return balance >= this.calculatedTotalPoints();
  });

  selectedUserHasOpenTab = computed(() => {
    const user = this.selectedUser();
    if (!user) return false;
    return this.openTabs().some(tab => tab.userId === user.id);
  });

  selectedUserExistingTab = computed(() => {
    const user = this.selectedUser();
    if (!user) return null;
    return this.openTabs().find(tab => tab.userId === user.id) || null;
  });

  canSubmit = computed(() => {
    if (this.submitting()) return false;
    if (this.items().length === 0) return false;

    switch (this.checkoutType()) {
      case CheckoutType.WALK_IN:
        if (this.hasPointsItems()) {
          const user = this.selectedWalkInUser();
          if (!user) return false;
          return this.walkInUserHasEnoughPoints();
        }
        return true;
      case CheckoutType.OVERHEAD:
        return true;
      case CheckoutType.NEW_TAB:
        if (this.newTabType() === PosTabType.User) {
          return this.selectedUser() !== null && !this.selectedUserHasOpenTab();
        } else {
          return this.visitorAlias().trim().length > 0;
        }
      case CheckoutType.EXISTING_TAB:
        return this.selectedTab() !== null;
    }
  });

  ngOnInit(): void {
    this.items.set(this.data.items);
    this.totalMoneyCents.set(this.data.totalMoneyCents);
    this.totalPoints.set(this.data.totalPoints);

    this.loadOpenTabs();
  }

  loadOpenTabs(search?: string): void {
    this.tabsLoading.set(true);
    this.posTabsApi.findOpenTabs({ search })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.tabsLoading.set(false)),
      )
      .subscribe({
        next: (tabs) => this.openTabs.set(tabs),
        error: () => this.openTabs.set([]),
      });
  }

  onTabSearch(query: string): void {
    this.tabSearchQuery.set(query);
    this.loadOpenTabs(query);
  }

  searchUsers(query: string): void {
    this.searchUsersGeneric(query, this.users, this.usersLoading);
  }

  searchWalkInUsers(query: string): void {
    this.searchUsersGeneric(query, this.walkInUsers, this.walkInUsersLoading);
  }

  private searchUsersGeneric(
    query: string,
    resultsSignal: WritableSignal<PosUserDto[]>,
    loadingSignal: WritableSignal<boolean>,
  ): void {
    if (query.length < 2) {
      resultsSignal.set([]);
      return;
    }

    loadingSignal.set(true);
    this.posTabsApi.searchUsers({ search: query })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => loadingSignal.set(false)),
      )
      .subscribe({
        next: (users) => resultsSignal.set(users),
        error: () => resultsSignal.set([]),
      });
  }

  selectUser(user: PosUserDto): void {
    this.selectedUser.set(user);
    this.userSearchQuery.set(`${ user.firstName } ${ user.lastName }`);
  }

  selectWalkInUser(user: PosUserDto): void {
    this.selectedWalkInUser.set(user);
    this.walkInUserSearchQuery.set(`${ user.firstName } ${ user.lastName }`);
  }

  toggleItemPaymentType(index: number): void {
    this.items.update(items => {
      const newItems = [ ...items ];
      const item = newItems[index];
      if (item.canPayWithPoints) {
        newItems[index] = {
          ...item,
          paymentType: item.paymentType === PaymentType.Money ? PaymentType.Points : PaymentType.Money,
        };
      }
      return newItems;
    });
  }

  displayUser(user: PosUserDto | null): string {
    return user ? `${ user.firstName } ${ user.lastName }` : '';
  }

  selectTab(tab: OpenTabSummaryDto): void {
    this.selectedTab.set(tab);
  }

  getTabDisplayName = getTabDisplayName;

  async onSubmit(): Promise<void> {
    if (!this.canSubmit()) return;

    this.submitting.set(true);

    try {
      let tabId: string | undefined;

      if (this.checkoutType() === CheckoutType.NEW_TAB) {
        const newTab = await this.createNewTab();
        if (!newTab) {
          this.submitting.set(false);
          return;
        }
        tabId = newTab.id;
      } else if (this.checkoutType() === CheckoutType.EXISTING_TAB) {
        const tab = this.selectedTab();
        if (!tab) {
          this.submitting.set(false);
          return;
        }
        tabId = tab.id;
      }

      const orderDto: CreateOrderDto = {
        tabId: tabId,
        isOverhead: this.checkoutType() === CheckoutType.OVERHEAD,
        ...(this.data.sessionId ? { sessionId: this.data.sessionId } : {}),
        items: this.items().map(item => ({
          productId: item.productId,
          qty: item.qty,
          paymentType: item.paymentType,
          ...(item.ingredients?.length ? { ingredients: item.ingredients } : {}),
        })),
      };

      if (this.checkoutType() === CheckoutType.WALK_IN) {
        orderDto.paymentMethod = this.paymentMethod();
        if (this.paymentMethod() === PaymentMethod.Card && this.currentVariableSymbol()) {
          orderDto.variableSymbol = this.currentVariableSymbol()!;
        }
        if (this.hasPointsItems() && this.selectedWalkInUser()) {
          orderDto.userId = this.selectedWalkInUser()!.id;
        }
      }

      this.posOrdersApi.createOrder(orderDto)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (order) => {
            const tabName = this.getTabNameForOrder();
            const paymentMethod = this.checkoutType() === CheckoutType.WALK_IN
              ? this.paymentMethod()
              : undefined;
            this.recentOrdersService.addOrder(order, { tabName, paymentMethod });
            this.notification.success('pos.checkout.success');
            this.dialogRef.close({ success: true, order });
          },
          error: () => {
            this.notification.error('pos.checkout.error');
            this.submitting.set(false);
          },
        });
    } catch {
      this.submitting.set(false);
    }
  }

  private createNewTab(): Promise<PosTab | null> {
    return new Promise((resolve) => {
      if (this.newTabType() === PosTabType.User) {
        const user = this.selectedUser();
        if (!user) {
          resolve(null);
          return;
        }

        this.posTabsApi.createUserTab({ body: { userId: user.id, ...(this.data.sessionId ? { sessionId: this.data.sessionId } : {}) } })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (tab) => resolve(tab),
            error: () => {
              this.notification.error('pos.checkout.tab_create_error');
              resolve(null);
            },
          });
      } else {
        const alias = this.visitorAlias().trim();
        if (!alias) {
          resolve(null);
          return;
        }

        this.posTabsApi.createVisitorTab({ body: { alias, ...(this.data.sessionId ? { sessionId: this.data.sessionId } : {}) } })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (tab) => resolve(tab),
            error: () => {
              this.notification.error('pos.checkout.tab_create_error');
              resolve(null);
            },
          });
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private getTabNameForOrder(): string | undefined {
    switch (this.checkoutType()) {
      case CheckoutType.EXISTING_TAB:
        return this.getTabDisplayName(this.selectedTab()!);
      case CheckoutType.NEW_TAB:
        if (this.newTabType() === PosTabType.User) {
          const user = this.selectedUser();
          return user ? `${ user.firstName } ${ user.lastName }` : undefined;
        } else {
          return this.visitorAlias().trim() || undefined;
        }
      default:
        return undefined;
    }
  }
}
